import React, {
  memo,
  type ReactElement,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { useDispatch } from 'react-redux';
import { Logger } from '@saviynt/common';
import { Box, Checkbox, type MenuOption } from '@saviynt/design-system';
import cx from 'classnames';

import { SodControls, SodSort } from '../../../../shared/components';
import SodColumnsList from '../../../../shared/components/SodColumnsList';
import {
  ABORT_ERROR,
  DEFAULT_AD_FILTER_PARAMS,
  IS_READ_ONLY,
  RENDER_MODES,
} from '../../../../shared/constants';
import {
  useInvertedLocalize,
  useTypedSelector,
} from '../../../../shared/hooks';
import { type AdvancedFilterState } from '../../../../shared/types';
import { type ColumnState } from '../../data-layer/violationsTabTypes';
import { violationsTabActions } from '../../data-store/violationsTabSlice';
import {
  useFilterCache,
  useGetQueryMap,
  useGetSortMenu,
  useSetFilterDelta,
} from '../../hooks';

import './ViolationsControls.css';

interface ViolationsControlsProps {
  isListMode: boolean;
  isStickyControls?: boolean;
  isInsightsEnabled?: boolean;
}

/**
 * ViolationsControls
 */
function ViolationsControls({
  isListMode,
  isStickyControls,
  isInsightsEnabled,
}: ViolationsControlsProps): ReactElement {
  const l = useInvertedLocalize();
  const dispatch = useDispatch();

  const {
    controls: {
      filters,
      tabFilter,
      filterCache,
      activeFilters,
      isInsightsOpen,
      activeColumns,
      sortInAction,
    },
    list: { totalCount, isListLoading },
    bulkaction: { isBulkChecked, isFooterVisible },
  } = useTypedSelector((state) => state.aag.violationsTab);

  const { isMenuPinned } = useTypedSelector((state) => state.user);

  const {
    fields,
    fieldName,
    actions,
    actionName,
    handleFieldClick,
    handleActionClick,
  } = useGetSortMenu(sortInAction);

  const queryMap = useGetQueryMap();
  const { setDelta } = useSetFilterDelta();
  const { getFromCache, setCache } = useFilterCache(
    filterCache,
    violationsTabActions.setFiltersCache
  );

  const isSticky = Boolean(isStickyControls && !isListMode);
  const isClearBtn = useMemo(
    () => Boolean(activeFilters.length > 0),
    [activeFilters]
  );

  // For Filters
  const abortRef = useRef<((reason: string) => void) | null>(null);
  const lastRequestedKeyRef = useRef<string | null>(null);

  const handleFilterSearch = useCallback(
    async <T extends string>(
      key: T,
      searchText: T
    ): Promise<MenuOption[] | undefined | false> => {
      const cacheResponse = getFromCache(key, searchText);

      if (cacheResponse) {
        return cacheResponse;
      }

      // Abort shoild only happen when the filter is same for search
      if (abortRef.current && lastRequestedKeyRef.current === key) {
        abortRef.current('New Search');
      }

      lastRequestedKeyRef.current = key;

      try {
        const fn = queryMap[key];

        const controller = fn({
          ...DEFAULT_AD_FILTER_PARAMS,
          search: searchText,
          key,
        });

        abortRef.current = () => controller.abort();

        const response = await controller.unwrap();

        const options = response as MenuOption[];

        setCache(key, searchText, options);

        return options;
      } catch (error) {
        const err = error as { name: string; message: string };

        Logger.log('error', err.message);

        if (err.name === ABORT_ERROR) {
          return false;
        }

        return undefined;
      }
    },
    [queryMap, getFromCache, setCache]
  );

  const handleOptionsClick = useCallback(
    (key: string, method: string, selectedOptions: MenuOption[]) => {
      if (selectedOptions.length === 0) {
        const newActiveFilters = activeFilters.filter(
          (filter) => filter.value !== key
        );

        setDelta(activeFilters, newActiveFilters);

        return;
      }

      const isFilterAvailable = activeFilters.find(
        (filter) => filter.value === key
      );
      const newActiveFilters = isFilterAvailable
        ? activeFilters.map((filter) => {
            if (filter.value === key) {
              return {
                ...filter,
                options: selectedOptions,
              };
            }

            return filter;
          })
        : [
            ...activeFilters,
            {
              value: key,
              method,
              options: selectedOptions,
            },
          ];

      setDelta(activeFilters, newActiveFilters);
    },
    [activeFilters, setDelta]
  );

  const handleFilterChange = useCallback(
    (
      selectedFilter: Pick<
        AdvancedFilterState,
        'value' | 'isChecked' | 'method'
      >
    ) => {
      const isFilterApplied = activeFilters.find(
        (filter) => filter.value === selectedFilter.value
      );

      if (selectedFilter.isChecked && isFilterApplied) {
        handleOptionsClick(selectedFilter.value, selectedFilter.method, []);
      }

      const modifiedFilters = filters.map((filter) => {
        if (selectedFilter.value === filter.value) {
          return {
            ...filter,
            isChecked: !filter.isChecked,
          };
        }

        return filter;
      });

      dispatch(violationsTabActions.setAdvancedFilters(modifiedFilters));
    },
    [dispatch, filters, activeFilters, handleOptionsClick]
  );

  const handleClearBtn = useCallback(() => {
    setDelta(activeFilters, []);
  }, [activeFilters, setDelta]);

  // For sidepanel and insights aside
  const handleInsights = useCallback(() => {
    dispatch(violationsTabActions.setIsInsightsOpen(!isInsightsOpen));
  }, [isInsightsOpen, dispatch]);

  const handleModeChange = useCallback(
    (mode: number) => {
      if (mode === 1) {
        dispatch(violationsTabActions.setRenderMode(RENDER_MODES.GRAPH));

        /* IS_READ_ONLY */
        dispatch(violationsTabActions.setIsInsightsOpen(false));

        return;
      }

      dispatch(violationsTabActions.setRenderMode(RENDER_MODES.LIST));
    },
    [dispatch]
  );

  // For tabs
  const handleTabChange = useCallback(
    (value: string) => {
      dispatch(violationsTabActions.setTabInAction(value));
    },
    [dispatch]
  );

  // For search
  const handleSearch = useCallback(
    (searchText: string) => {
      dispatch(violationsTabActions.setSearchKey(searchText));
    },
    [dispatch]
  );

  // For column filter
  const handleColumnsChange = useCallback(
    (columns: ColumnState[]) => {
      dispatch(violationsTabActions.setViewColumns(columns));
    },
    [dispatch]
  );

  const wrapperClasses = cx(
    'Sod-ViolationsControls',
    isSticky && 'Sod-ViolationsControls--isSticky',
    isMenuPinned && 'Sod-ViolationsControls--isMenuPinned'
  );

  return (
    <Box className={wrapperClasses} dataTheme={isSticky ? 'light' : null}>
      <SodControls
        tabs={tabFilter}
        filters={filters}
        activeFilters={activeFilters}
        maxEnabledFilterCount={12}
        searchPlaceholder={l('Search')}
        isClearBtn={isClearBtn}
        isInsightsDisabled={
          Boolean(isInsightsEnabled) || isFooterVisible || isSticky
        }
        isInsightsOpen={Boolean(isInsightsEnabled && isInsightsOpen)}
        currentMode={isListMode ? 2 : 1}
        setIsInsightsOpen={handleInsights}
        handleFilterChange={handleFilterChange}
        handleFilterSearch={handleFilterSearch}
        handleOptionsClick={handleOptionsClick}
        handleClearBtn={handleClearBtn}
        handleTabChange={handleTabChange}
        handleModeChange={handleModeChange}
        handleSearch={handleSearch}
        isDisabled={isListLoading}
        isDarkTheme={!isSticky && !isListMode}
      />
      {isListMode && (
        <Box className='Sod-ViolationsControls-groupControls'>
          <Checkbox
            showHoverEffect
            isChecked={isBulkChecked}
            onChange={() => {
              dispatch(violationsTabActions.setIsBulkChecked(!isBulkChecked));
            }}
            isDisabled={
              isListLoading ||
              totalCount === null ||
              totalCount === 0 ||
              IS_READ_ONLY
            }
            textPosition='after'
            size='small'
            label={l('Select All ({count})', { count: totalCount ?? '-' })}
          />
          <Box className='Sod-ViolationsControls-groupControlsArrangeBtns'>
            <SodSort
              fields={fields}
              fieldName={fieldName}
              actions={actions}
              actionName={actionName}
              handleFieldClick={handleFieldClick}
              handleActionClick={handleActionClick}
              isDisabled={isListLoading}
            />
            <Box
              tag='span'
              className='Sod-ViolationsControls-groupControlsArrangeBtnsDivider'
            />
            <SodColumnsList
              columns={activeColumns}
              isDisabled={isListLoading}
              handleColumnsChange={handleColumnsChange}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
}

ViolationsControls.defaultProps = {
  isStickyControls: false,
  isInsightsEnabled: false,
};

export default memo(ViolationsControls);
